// js/common/search/predictive-search.js
import { animate as animate9 } from 'vendor';

if (!window.customElements.get('predictive-search')) {
	function debounce(fn, delay) {
		let timer = null;
		return (...args) => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				fn.apply(this, args);
			}, delay);
		};
	}

	var cachedMap = /* @__PURE__ */ new Map();
	function cachedFetch(url, options) {
		const cacheKey = url;
		if (cachedMap.has(cacheKey)) {
			return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
		}
		return fetch(url, options).then((response) => {
			if (response.status === 200) {
				const contentType = response.headers.get('Content-Type');
				if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
					response
						.clone()
						.text()
						.then((content) => {
							cachedMap.set(cacheKey, content);
						});
				}
			}
			return response;
		});
	}

	var PredictiveSearch = class extends HTMLElement {
		constructor() {
			super();
			this.attachShadow({ mode: 'open' });
			this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="idle"></slot>`));
		}
		connectedCallback() {
			this._searchForm = this.closest('form');
			this._queryInput = this._searchForm.elements['q'];
			this._searchForm.addEventListener('submit', this._onFormSubmitted.bind(this));
			this._searchForm.addEventListener('reset', this._onSearchCleared.bind(this));
			this._queryInput.addEventListener('input', debounce(this._onInputChanged.bind(this), this.autoCompleteDelay));
		}
		/**
		 * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
		 * refresh too often, so we recommend to keep the default one
		 */
		get autoCompleteDelay() {
			return 280;
		}
		/**
		 * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
		 * search is simply disabled and only the standard search is used
		 */
		supportsPredictiveApi() {
			return JSON.parse(document.getElementById('shopify-features').innerHTML)['predictiveSearch'];
		}
		/**
		 * Check if the input is not empty, and if so, trigger the predictive search
		 */
		_onInputChanged() {
			if (this._queryInput.value === '') {
				return this._onSearchCleared();
			}
			this._abortController?.abort();
			this._abortController = new AbortController();
			try {
				return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
			} catch (e) {
				if (e.name !== 'AbortError') {
					throw e;
				}
			}
		}
		/**
		 * Prevent the form submission if the query is empty
		 */
		_onFormSubmitted(event) {
			if (this._queryInput.value === '') {
				return event.preventDefault();
			}
		}
		/**
		 * Do the actual predictive search
		 */
		async _doPredictiveSearch() {
			await this._transitionToSlot('loading');
			const queryParams = `q=${encodeURIComponent(this._queryInput.value)}&section_id=${this.getAttribute(
				'section-id'
			)}&resources[limit]=10&resources[limit_scope]=each`;
			const nodeElement = new DOMParser().parseFromString(
				await (
					await cachedFetch(`${window.Shopify.routes.root}search/suggest?${queryParams}`, { signal: this._abortController.signal })
				).text(),
				'text/html'
			);
			this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
			return this._transitionToSlot('results');
		}
		/**
		 * For merchants using store that do not support the predictive search, we have to fallback to standard search. Unfortunately,
		 * the standard search is less convenient, and we have to simulate one request for each desired resource type
		 */
		async _doFallbackSearch() {
			await this._transitionToSlot('loading');
			const queryParams = `q=${this._queryInput.value}&section_id=${this.getAttribute(
				'section-id'
			)}&resources[limit]=10&resources[limit_scope]=each`;
			const nodeElement = new DOMParser().parseFromString(
				await (await cachedFetch(`${window.Shopify.routes.root}search?${queryParams}`, { signal: this._abortController.signal })).text(),
				'text/html'
			);
			this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
			return this._transitionToSlot('results');
		}
		/**
		 * If any search is pending, we abort them, and transition to the idle slot
		 */
		_onSearchCleared() {
			this._abortController?.abort();
			this._queryInput.focus();
			return this._transitionToSlot('idle');
		}
		/**
		 * Transition between different slot. To prevent useless animation, the slot does not transition if we go to the same slot
		 */
		async _transitionToSlot(toSlotName) {
			if (this.shadowRoot.firstElementChild.name === toSlotName) {
				return;
			}
			await animate9(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [1, 0] }, { duration: 0.1 }).finished;
			this.shadowRoot.firstElementChild.setAttribute('name', toSlotName);
			return animate9(
				this.shadowRoot.firstElementChild.assignedNodes(),
				{ opacity: [0, 1], transform: ['translateY(5px)', 'translateY(0)'] },
				{ duration: 0.1 }
			).finished;
		}
	};
	window.customElements.define('predictive-search', PredictiveSearch);
}
